<script setup lang="ts">
import { Comment, Text, computed, onMounted, ref, useSlots, type VNode } from 'vue';
import { DsButtonPlay, DsMediaModal } from '@coloplast/design-system';
import { normalizeVideo, type NormalizedVideo, type VideoProvider } from 'shared/utils/videoEmbed.utils';
import axios from 'axios';
import '@coloplast/design-system/dist/components/button/ds-button-play.css';
import '@coloplast/design-system/dist/components/media-modal/ds-media-modal.css';
import '@coloplast/design-system/dist/components/caption/ds-caption.css';

type MediaModalVideoProvider = Exclude<VideoProvider, 'unknown'>;
type MediaModalAspectRatio = '16/9' | '9/16' | '4/3' | '3/4' | '1/1';

type MediaModalCaption = {
	name?: string;
	role?: string;
	description?: string;
};

type MediaModalVideoItem = {
	type: MediaModalVideoProvider;
	src: string;
	title: string;
	autoplay: boolean;
	muted: boolean;
	caption?: MediaModalCaption;
};

type Props = {
	videoUrl?: string | null;
	videoTitle?: string | null;
	playLabel?: string | null;
	thumbnailImageUrl?: string | null;
	sourceName?: string | null;
	sourceDetails?: string | null;
	caption?: string | null;
	quote?: string | null;
	orientationCssClass?: string;
	mediaModalAspectRatio?: MediaModalAspectRatio | '';
	autoplayVideos?: boolean;
	videoModalCloseLabel?: string;
};

const props = withDefaults(defineProps<Props>(), {
	mediaModalAspectRatio: '9/16',
	autoplayVideos: true,
	videoModalCloseLabel: 'Close',
});

const videoThumbnailUrl = ref<string | null>(props.thumbnailImageUrl || null);
const isVideoPlaying = ref(false);
const slots = useSlots();

function hasMeaningfulSlotContent(node: VNode) {
	if (node.type === Comment) return false;
	if (node.type === Text) {
		return typeof node.children === 'string' && node.children.trim().length > 0;
	}

	return true;
}

function normalizeTestimonialVideo(videoUrl?: string | null): NormalizedVideo | null {
	if (!videoUrl) return null;

	const detectedVideo = normalizeVideo(videoUrl);

	if (detectedVideo.provider === 'youtube') {
		return normalizeVideo(videoUrl, {
			youtubeNoCookie: !(globalThis as unknown as Window).marketingPermissions,
			params: {
				autoplay: null,
				playsinline: null,
				controls: null,
				enablejsapi: 1,
				showinfo: 0,
			},
		});
	}

	if (detectedVideo.provider === 'vimeo') {
		return normalizeVideo(videoUrl, {
			params: {
				autoplay: null,
				playsinline: null,
				title: null,
				byline: null,
				portrait: null,
				dnt: null,
				api: 1,
			},
		});
	}

	return detectedVideo;
}

const normalizedVideo = computed(() => normalizeTestimonialVideo(props.videoUrl));

function isMediaModalVideoProvider(provider: VideoProvider): provider is MediaModalVideoProvider {
	return provider === 'youtube' || provider === 'vimeo';
}

async function setFallbackThumbnail() {
	const video = normalizedVideo.value;
	if (!video?.id || props.thumbnailImageUrl) return;

	if (video.provider === 'youtube') {
		videoThumbnailUrl.value = `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`;
		return;
	}

	if (video.provider !== 'vimeo') return;

	const vimeoJson = `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${video.id}&format=json`;
	try {
		const response = await axios.get<{ thumbnail_url?: string }>(vimeoJson);
		videoThumbnailUrl.value = response?.data?.thumbnail_url || null;
	} catch (error) {
		console.log(error);
	}
}

const closeModal = () => {
	isVideoPlaying.value = false;
};

function openModal() {
	isVideoPlaying.value = true;
}

function loadScript() {
	const tag = document.createElement('script');
	tag.src = 'https://www.youtube.com/iframe_api';

	const firstScriptTag = document.getElementsByTagName('script')[0];
	firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
}

const videoTitle = computed(
	() =>
		props.videoTitle?.trim() ||
		props.playLabel?.trim() ||
		'',
);
const playLabel = computed(
	() => props.playLabel?.trim() || videoTitle.value || 'Play video',
);
const hasPosterSlot = computed(
	() => slots.default?.().some(hasMeaningfulSlotContent) === true,
);

const mediaModalAspectRatio = computed<MediaModalAspectRatio>(
	() => props.mediaModalAspectRatio || '9/16',
);
const mediaModalCaption = computed<MediaModalCaption | undefined>(() => {
	const caption = {
		name: props.sourceName || props.caption || undefined,
		role: props.sourceDetails || undefined,
		description: props.quote || undefined,
	};

	return caption.name || caption.role || caption.description
		? caption
		: undefined;
});
const mediaModalItems = computed<MediaModalVideoItem[]>(() => {
	const video = normalizedVideo.value;
	if (!video?.embedUrl || !isMediaModalVideoProvider(video.provider)) return [];

	return [
		{
			type: video.provider,
			src: video.embedUrl,
			title: videoTitle.value,
			autoplay: props.autoplayVideos,
			muted: video.provider === 'youtube' && props.autoplayVideos,
			caption: mediaModalCaption.value,
		},
	];
});

onMounted(() => {
	setFallbackThumbnail();
	if (
		normalizedVideo.value?.provider === 'youtube' &&
		document.querySelectorAll(
			'script[src="https://www.youtube.com/iframe_api"]',
		).length === 0
	) {
		loadScript();
	}
});
</script>

<template>
	<div
		v-if="videoUrl"
		class="slide-video-placeholder c-consumer-testimonials__slide-picture"
		:class="orientationCssClass"
		data-testid="video-placeholder"
	>
		<slot v-if="hasPosterSlot"></slot>
		<img
			v-else-if="videoThumbnailUrl"
			:src="videoThumbnailUrl"
			loading="lazy"
			alt=""
			data-testid="placeholder-image"
		/>

		<span class="slide-play-button">
			<DsButtonPlay
				:aria-label-play="playLabel"
				:play-handler="openModal"
				aria-haspopup="dialog"
				size="sm"
			>
			</DsButtonPlay>
		</span>
	</div>

	<DsMediaModal
		v-if="mediaModalItems.length"
		data-testid="testimonials-video-player"
		:is-open="isVideoPlaying"
		:items="mediaModalItems"
		:aspect-ratio="mediaModalAspectRatio"
		:close-label="videoModalCloseLabel"
		:media-stage-max-height="1200"
		@close-modal="closeModal"
	/>
</template>
